import { type CamelCasedProperties } from 'type-fest'

import { type ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { type TrafficImpactMapping } from 'types/api/attacks/details/create'
import { type K8sObject, type Strategy } from 'types/api/kubernetes/kubernetes'

export type AttackCommand =
  | 'blackhole'
  | 'cpu'
  | 'gpu'
  | 'disk'
  | 'dns'
  | 'io'
  | 'latency'
  | 'memory'
  | 'packet_loss'
  | 'process_killer'
  | 'shutdown'
  | 'time_travel'
  | 'collect_certs'
  | 'process_exhaustion'

export type AttackName =
  | 'CPU'
  | 'GPU'
  | 'Disk'
  | 'IO'
  | 'Memory'
  | 'Process Killer'
  | 'Shutdown'
  | 'Time Travel'
  | 'Blackhole'
  | 'DNS'
  | 'Latency'
  | 'Packet Loss'
  | 'Certificate Expiry'
  | 'Process Exhaustion'

export enum AttackTypeCategory {
  Network = 'Network',
  State = 'State',
  Resource = 'Resource'
}

export type AttackCliArg = string | number
export type AttackCliArgs = [AttackCommand, ...AttackCliArg[]]

type Protocol = 'TCP' | 'UDP' | 'ICMP'
type Mode = 'r' | 'rw' | 'w'

export interface SnakeCaseAttackArgs {
  type?: AttackCommand | AttackName
  cli_args?: AttackCliArgs
  providers?: string[]
  traffic_impact_mapping?: TrafficImpactMapping
  all_cores?: boolean
  block_count?: number
  block_size?: number
  cores?: number
  corrupt?: boolean
  delay?: number
  device?: string
  directory?: string
  disable_n_t_p?: boolean
  egress_ports?: string
  exact?: boolean
  full_match?: boolean
  gigabytes?: number
  group?: string
  hostnames?: string
  ingress_ports?: string
  interval?: number
  ip_addresses?: string
  kill_children?: boolean
  length?: number
  megabytes?: number
  milliseconds?: number
  mode?: Mode
  newest?: boolean
  offset?: number
  oldest?: boolean
  percent?: number
  process?: string
  protocol?: Protocol
  reboot?: boolean
  source_ports?: string
  user?: string
  workers?: number
}

export type AttackArgs = SnakeCaseAttackArgs &
  CamelCasedProperties<SnakeCaseAttackArgs>

export interface SnakeCaseAttackCommandConfiguration {
  infraCommandType: AttackCommand
  infraCommandArgs: SnakeCaseAttackArgs
}

export interface AttackCommandConfiguration {
  infraCommandType: AttackCommand | AttackName
  infraCommandArgs: AttackArgs
}

export enum SelectionType {
  Exact = 'EXACT',
  All = 'ALL',
  Any = 'ANY'
}

export interface ContainerSelection {
  selectionType?: SelectionType
  containerNames?: string[]
}

export interface Names {
  clusterId: string
  kind: string
  name: string
  namespace: string
  owningTeamId?: string
}

export interface LabelSelection {
  labelSelector: string
  clusterId: string
  allNamespaces?: boolean
  namespaces?: string[] | null
}

export interface BaseTargetingStrategy {
  isAll?: boolean
  ids?: string[]
}

export interface HostTargetingStrategy extends BaseTargetingStrategy {
  type: ProvenanceTargetType.ProcessData
  tags?: {
    [tagName: string]: string[]
  }
  multiSelectTags?: {
    [tagName: string]: string[]
  }
}

export interface ContainerTargetingStrategy extends BaseTargetingStrategy {
  type: ProvenanceTargetType.Container
  labels?: {
    [labelName: string]: string[]
  }
  multiSelectLabels?: {
    [labelName: string]: string[]
  }
}

export interface KubernetesTargetingStrategy extends BaseTargetingStrategy {
  type: ProvenanceTargetType.Kubernetes
  names?: Names[]
  labelSelection?: LabelSelection // used when targetingStrategyType is 'Selectors'
  containerSelection?: ContainerSelection
}

export type TargetingStrategy =
  | HostTargetingStrategy
  | ContainerTargetingStrategy
  | KubernetesTargetingStrategy

export interface TargetEntry {
  serviceId?: string
  service_id?: string
  targetingStrategy: TargetingStrategy
}

export type AttackTargetingConfiguration = TargetEntry[]

export interface ExactSampling {
  type: 'Exact'
  count?: number
}

export interface EvenSampling {
  type: 'Even'
  percent?: number
  count?: number
}

export type AttackSamplingConfiguration = ExactSampling | EvenSampling

export interface SnakeCaseAttackConfiguration {
  command: SnakeCaseAttackCommandConfiguration
  targets: AttackTargetingConfiguration
  sampling: AttackSamplingConfiguration
}

export interface AttackConfiguration {
  command: AttackCommandConfiguration
  targets: AttackTargetingConfiguration
  sampling: AttackSamplingConfiguration
}

export type AttackConfigurationTargetType = 'Host' | 'Container' | 'Kubernetes'
export type CreateServiceAttackResponseItemBase = {
  serviceId: string
  type: AttackConfigurationTargetType
}
export type CreateInfraServiceAttackResponse = [
  CreateServiceAttackResponseItemBase & {
    attackId: string
  }
]
export type CreateKubernetesServiceAttackResponse = [
  CreateServiceAttackResponseItemBase & { k8sAttackId: string }
]

export interface CallBack {
  url?: string
}

export interface Command {
  providers?: string[]
  trafficImpactMapping?: TrafficImpactMapping
  cliArgs?: AttackCliArgs
}

export interface InfraCommand {
  templateId?: string
  infraCommandType?: InfraCommandType
  infraCommandArgs?: InfraCommandArgs
  maxExpectedLength?: number
  length?: number
}

export enum InfraCommandType {
  CPU = 'CPU',
  GPU = 'GPU',
  IO = 'IO',
  Memory = 'Memory',
  Disk = 'Disk',
  Blackhole = 'Blackhole',
  DNS = 'DNS',
  Latency = 'Latency',
  PacketLoss = 'Packet_Loss',
  Shutdown = 'Shutdown',
  TimeTravel = 'Time_Travel',
  ProcessKiller = 'Process_Killer',
  Unknown = 'Unknown'
}

export type InfraCommandArgs = Partial<
  Omit<SnakeCaseAttackArgs, 'cli_args' | 'type'>
>

export interface InfraTarget {
  templateId?: string
  strategyType?: InfraStrategyType
  targetType?: InfraTargetType
  strategy?: Strategy
}

export enum InfraStrategyType {
  Exact = 'Exact',
  Regex = 'Regex',
  Random = 'Random'
}

export enum InfraTargetType {
  Host = 'Host',
  Container = 'Container',
  Docker = 'Docker',
  Kubernetes = 'Kubernetes'
}

export interface StrategyObject {
  k8sObjects?: Array<any>
  k8sObjectsInternal?: K8sObject[]
  count?: number
  percentage?: number
  containerSelection?: ContainerSelection
}

export interface Target {
  strategy?: StrategyObject
}

export interface TargetObject {
  strategy?: StrategyObject
}
